const DEFAULT_EXTENSION = '.pdf'
const GENERIC_FILE_NAMES = new Set([
  'blob.pdf',
  'doc.pdf',
  'document.pdf',
  'download.pdf',
  'file.pdf'
])

function headerValue (headers, name) {
  if (!headers || typeof headers !== 'object') return ''
  const expected = String(name || '').toLowerCase()
  const key = Object.keys(headers).find(
    candidate => String(candidate).toLowerCase() === expected
  )
  return key ? String(headers[key] || '') : ''
}

function unquote (value) {
  const text = String(value || '').trim()
  if (text.length >= 2 && text.startsWith('"') && text.endsWith('"')) {
    return text.slice(1, -1).replace(/\\"/g, '"')
  }
  return text
}

function normalizeUnicode (value) {
  const text = String(value || '')
  if (typeof text.normalize !== 'function') return text
  try {
    return text.normalize('NFC')
  } catch (error) {
    return text
  }
}

function decodeRfc5987Value (value) {
  const text = unquote(value)
  const match = text.match(/^([^']*)'[^']*'(.*)$/)
  const encoded = match ? match[2] : text
  try {
    return decodeURIComponent(encoded)
  } catch (error) {
    return encoded
  }
}

function repairLatin1Utf8Mojibake (value) {
  const text = String(value || '')
  if (!text || !/[\u0080-\u009fÃÂãæåäçèé]/.test(text)) return text

  const characters = Array.from(text)
  if (characters.some(character => character.charCodeAt(0) > 255)) {
    return text
  }

  const percentEncoded = characters
    .map(character => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`)
    .join('')

  try {
    const repaired = decodeURIComponent(percentEncoded)
    return repaired && repaired !== text ? repaired : text
  } catch (error) {
    return text
  }
}

export function extractContentDispositionFileName (headers) {
  const disposition = headerValue(headers, 'content-disposition')
  if (!disposition) return ''

  const encodedMatch = disposition.match(
    /(?:^|;)\s*filename\*\s*=\s*("[^"]*"|[^;]*)/i
  )
  if (encodedMatch) {
    return repairLatin1Utf8Mojibake(
      decodeRfc5987Value(encodedMatch[1])
    )
  }

  const plainMatch = disposition.match(
    /(?:^|;)\s*filename\s*=\s*("[^"]*"|[^;]*)/i
  )
  return plainMatch
    ? repairLatin1Utf8Mojibake(unquote(plainMatch[1]))
    : ''
}

function ensureExtension (fileName, extension) {
  const normalizedExtension = String(extension || DEFAULT_EXTENSION)
  if (!normalizedExtension) return fileName
  return fileName.toLowerCase().endsWith(normalizedExtension.toLowerCase())
    ? fileName
    : `${fileName}${normalizedExtension}`
}

function sanitizeBaseName (value) {
  return normalizeUnicode(value)
    .replace(/[<>:"/\\|?*\u0000-\u001f\u007f]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[. ]+$/g, '')
    .replace(/^\.+/, '_')
    .replace(/^_+/, '_')
}

export function sanitizeDownloadFileName (
  value,
  fallback = 'document.pdf',
  extension = DEFAULT_EXTENSION
) {
  const fallbackText = String(fallback || 'document.pdf')
  let fileName = sanitizeBaseName(repairLatin1Utf8Mojibake(value))

  if (!fileName) fileName = sanitizeBaseName(fallbackText)
  if (!fileName) fileName = 'document'

  fileName = ensureExtension(fileName, extension)

  if (/^(con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i.test(fileName)) {
    fileName = `_${fileName}`
  }

  const maxLength = 180
  if (fileName.length > maxLength) {
    const suffix = String(extension || DEFAULT_EXTENSION)
    const bodyLength = Math.max(1, maxLength - suffix.length)
    const body = fileName.toLowerCase().endsWith(suffix.toLowerCase())
      ? fileName.slice(0, -suffix.length)
      : fileName
    fileName = `${body.slice(0, bodyLength)}${suffix}`
  }
  return fileName
}

function isGenericFileName (fileName) {
  return GENERIC_FILE_NAMES.has(String(fileName || '').toLowerCase())
}

export function resolveDownloadFileName (
  headers,
  fallback,
  extension = DEFAULT_EXTENSION
) {
  const rawHeaderFileName = extractContentDispositionFileName(headers)
  if (!rawHeaderFileName) {
    return sanitizeDownloadFileName(fallback, 'document.pdf', extension)
  }

  const fromHeader = sanitizeDownloadFileName(
    rawHeaderFileName,
    fallback,
    extension
  )
  return isGenericFileName(fromHeader)
    ? sanitizeDownloadFileName(fallback, 'document.pdf', extension)
    : fromHeader
}

export function downloadBlobResponse (
  response,
  fallbackFileName,
  mimeType = 'application/pdf'
) {
  if (!response || response.data === undefined || response.data === null) {
    throw new Error('ダウンロード対象のファイルデータがありません。')
  }
  if (typeof Blob === 'undefined') {
    throw new Error('ブラウザがファイルデータに対応していません。')
  }

  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: mimeType })
  const fileName = resolveDownloadFileName(
    response.headers,
    fallbackFileName,
    '.pdf'
  )

  if (typeof navigator !== 'undefined' &&
      typeof navigator.msSaveOrOpenBlob === 'function') {
    navigator.msSaveOrOpenBlob(blob, fileName)
    return fileName
  }

  if (typeof document === 'undefined' || !document.body) {
    throw new Error('ファイルを保存できる画面環境ではありません。')
  }
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    throw new Error('ブラウザがファイル保存に対応していません。')
  }

  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = fileName
  anchor.rel = 'noopener'
  anchor.style.display = 'none'
  document.body.appendChild(anchor)

  try {
    anchor.click()
  } finally {
    if (anchor.parentNode) anchor.parentNode.removeChild(anchor)
    setTimeout(() => {
      if (typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(objectUrl)
      }
    }, 0)
  }
  return fileName
}
